import { BAG_SIZE } from '@/world/quickBag';
import { type Vec2, applyDeadzone, clampLength } from '@/core/vec';

/**
 * Unified input.
 *
 * Every system reads the same InputState regardless of source. Touch is the
 * primary target, but keyboard and gamepad are first-class so the game is
 * testable on a desktop and playable with a controller — retrofitting that
 * later always means rewriting whatever assumed touch.
 */

/**
 * `special` is the Spirit/magic slot. It exists now, unbound to any behaviour,
 * because the control layout has to be sized and reachable before Phase 4
 * decides what it casts — retrofitting a fourth button into a cluster tuned for
 * three means re-tuning all of them.
 */
export type Action =
  | 'interact'
  | 'attack'
  | 'dash'
  | 'special'
  | 'menu'
  | 'nextItem'
  | 'prevItem'
  | 'nextTool';

export const ACTIONS: readonly Action[] = [
  'interact',
  'attack',
  'dash',
  'special',
  'menu',
  'nextItem',
  'prevItem',
  'nextTool',
];

export interface InputState {
  /** Movement intent, length 0..1. */
  readonly move: Vec2;
  /** Actions held this frame. */
  readonly held: ReadonlySet<Action>;
  /** Actions that became held since the previous frame. */
  readonly pressed: ReadonlySet<Action>;
  /** Actions released since the previous frame. */
  readonly released: ReadonlySet<Action>;
  /** Hotbar slot requested this frame by a number key, or null. */
  readonly slotRequest: number | null;
}

const KEY_BINDINGS: Readonly<Record<string, Action>> = {
  Space: 'interact',
  Enter: 'interact',
  KeyE: 'interact',
  KeyJ: 'attack',
  KeyF: 'attack',
  KeyK: 'special',
  KeyQ: 'special',
  ShiftLeft: 'dash',
  ShiftRight: 'dash',
  Escape: 'menu',
  Tab: 'menu',
  KeyT: 'nextTool',
  KeyX: 'nextItem',
  KeyZ: 'prevItem',
  BracketRight: 'nextItem',
  BracketLeft: 'prevItem',
};

/**
 * Digit keys jump straight to a hotbar slot.
 *
 * Length is derived from BAG_SIZE rather than written out, so growing the
 * hotbar cannot silently leave the last slots unreachable from the keyboard —
 * which is exactly what happened when it went from six slots to eight.
 */
const SLOT_KEYS: readonly string[] = Array.from(
  { length: Math.min(BAG_SIZE, 9) },
  (_, index) => `Digit${index + 1}`,
);

/** Standard Gamepad layout: 0=cross, 1=circle, 2=square, 3=triangle, 9=start. */
const GAMEPAD_BINDINGS: Readonly<Record<number, Action>> = {
  0: 'interact',
  1: 'dash',
  2: 'attack',
  3: 'special',
  9: 'menu',
};

const GAMEPAD_DEADZONE = 0.25;

/** Source of movement/action input. Touch controls implement this too. */
export interface InputSource {
  /** Movement contribution; need not be normalised. */
  readMove(): Vec2;
  /** Actions currently held. */
  readHeld(): Iterable<Action>;
  /**
   * Actions pressed and already released since the last poll, if the source
   * tracks them. Optional so a source can ignore sub-frame taps.
   */
  takeLatched?(): Iterable<Action>;
}

export class InputManager {
  private readonly keysDown = new Set<string>();
  /**
   * Keys that went down since the last poll, whether or not they are still
   * down.
   *
   * Without this, a press and release inside a single frame vanishes: keydown
   * adds to keysDown and keyup removes it, both before update() ever looks.
   * At 60fps that is any tap under ~16ms — rare, but a dropped input is
   * miserable to diagnose and trivially avoidable.
   */
  private readonly keysPressedSincePoll = new Set<string>();
  private readonly sources: InputSource[] = [];

  private held: Set<Action> = new Set();
  private pressed: Set<Action> = new Set();
  private released: Set<Action> = new Set();
  private move: Vec2 = { x: 0, y: 0 };
  private slotRequest: number | null = null;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    // Let browser shortcuts through; only swallow keys the game actually binds.
    if (isBoundKey(event.code)) event.preventDefault();
    // Ignore auto-repeat: holding a key must not re-fire `pressed` every frame.
    if (!event.repeat) this.keysPressedSincePoll.add(event.code);
    this.keysDown.add(event.code);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.keysDown.delete(event.code);
  };

  /** Dropping held keys on blur prevents Katie walking off on her own. */
  private readonly onBlur = (): void => {
    this.keysDown.clear();
    this.keysPressedSincePoll.clear();
  };

  attach(target: Window = window): void {
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
  }

  detach(target: Window = window): void {
    target.removeEventListener('keydown', this.onKeyDown);
    target.removeEventListener('keyup', this.onKeyUp);
    target.removeEventListener('blur', this.onBlur);
  }

  addSource(source: InputSource): void {
    this.sources.push(source);
  }

  /** Call once per frame, before simulation. */
  update(): void {
    const previouslyHeld = this.held;
    const held = new Set<Action>();
    const move: Vec2 = { x: 0, y: 0 };
    /** Pressed and already released before we got here. */
    const latched = new Set<Action>();

    // Keyboard
    for (const code of this.keysDown) {
      const action = KEY_BINDINGS[code];
      if (action) held.add(action);
    }
    for (const code of this.keysPressedSincePoll) {
      const action = KEY_BINDINGS[code];
      if (action && !this.keysDown.has(code)) latched.add(action);
    }

    // Slot selection is edge-triggered from the same latch set, so it works
    // whether or not the key is still down when we get here.
    this.slotRequest = null;
    for (const code of [...this.keysPressedSincePoll, ...this.keysDown]) {
      const slot = SLOT_KEYS.indexOf(code);
      if (slot >= 0) {
        this.slotRequest = slot;
        break;
      }
    }
    this.keysPressedSincePoll.clear();
    if (this.keysDown.has('KeyA') || this.keysDown.has('ArrowLeft')) move.x -= 1;
    if (this.keysDown.has('KeyD') || this.keysDown.has('ArrowRight')) move.x += 1;
    if (this.keysDown.has('KeyW') || this.keysDown.has('ArrowUp')) move.y -= 1;
    if (this.keysDown.has('KeyS') || this.keysDown.has('ArrowDown')) move.y += 1;

    // Gamepad
    const pads = typeof navigator !== 'undefined' ? navigator.getGamepads?.() : null;
    for (const pad of pads ?? []) {
      if (!pad) continue;
      const stick = applyDeadzone(
        { x: pad.axes[0] ?? 0, y: pad.axes[1] ?? 0 },
        GAMEPAD_DEADZONE,
      );
      move.x += stick.x;
      move.y += stick.y;
      for (const [index, action] of Object.entries(GAMEPAD_BINDINGS)) {
        if (pad.buttons[Number(index)]?.pressed) held.add(action);
      }
    }

    // Touch and any other registered source
    for (const source of this.sources) {
      const contribution = source.readMove();
      move.x += contribution.x;
      move.y += contribution.y;
      for (const action of source.readHeld()) held.add(action);
      for (const action of source.takeLatched?.() ?? []) latched.add(action);
    }

    this.move = clampLength(move, 1);

    // A latched action counts as both pressed and released this frame: it
    // happened, and it is already over.
    this.pressed = difference(held, previouslyHeld);
    for (const action of latched) this.pressed.add(action);

    this.released = difference(previouslyHeld, held);
    for (const action of latched) {
      if (!held.has(action)) this.released.add(action);
    }

    this.held = held;
  }

  /**
   * Swallow this step's presses, leaving movement alone.
   *
   * For a press whose whole job was dismissing something — a catch card, a
   * prompt — so it cannot also act on the world behind it. Catching a fish and
   * pressing interact used to clear the card *and* cast the line again in the
   * same instant, which reads as the card never having been dismissible.
   */
  consumePresses(): void {
    this.pressed = new Set();
    this.slotRequest = null;
  }

  get state(): InputState {
    return {
      move: this.move,
      held: this.held,
      pressed: this.pressed,
      released: this.released,
      slotRequest: this.slotRequest,
    };
  }
}

function isBoundKey(code: string): boolean {
  return (
    code in KEY_BINDINGS ||
    ['KeyA', 'KeyD', 'KeyW', 'KeyS', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(
      code,
    ) ||
    SLOT_KEYS.includes(code)
  );
}

function difference(a: ReadonlySet<Action>, b: ReadonlySet<Action>): Set<Action> {
  const result = new Set<Action>();
  for (const value of a) if (!b.has(value)) result.add(value);
  return result;
}
